import { useRef, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";

const toDay = (value: string) => (value ? dayjs(value) : null);

export default function DateField({ label, value, onChange, error, helperText, onBlur }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string | null;
  onBlur?: () => void;
}) {
  const [day, setDay] = useState<Dayjs | null>(() => toDay(value));
  const [synced, setSynced] = useState(value);
  const [invalid, setInvalid] = useState(false);
  if (value !== synced) {
    setSynced(value);
    setDay(toDay(value));
    setInvalid(false);
  }

  const inputRef = useRef<HTMLInputElement>(null);
  const hasDigits = () => /\d/.test(inputRef.current?.value ?? "");
  const store = (stored: string) => {
    setSynced(stored);
    setInvalid(false);
    if (stored !== value) onChange(stored);
  };

  return (
    <DatePicker
      label={label} format="MM/DD/YYYY" value={day}
      onChange={(next, { validationError }) => {
        setDay(next);
        if (next !== null && validationError === null) store(next.format("YYYY-MM-DD"));
      }}
      enableAccessibleFieldDOMStructure={false}
      slotProps={{
        textField: {
          inputRef, size: "small", fullWidth: true, error: error || invalid,
          helperText: helperText ?? (invalid ? "Enter a full date (MM/DD/YYYY)" : undefined),
          onKeyUp: () => { if (!hasDigits()) store(""); },
          onBlur: () => {
            if (hasDigits()) setInvalid(!day?.isValid() || day.format("YYYY-MM-DD") !== synced);
            else store("");
            onBlur?.();
          },
        },
      }}
    />
  );
}
